/**
 * The situation a brain outside the sim is allowed to see: one aircraft's own state, what it was
 * told, where it is going, the traffic around it, the last of the frequency, and the values it may
 * speak. Plain JSON, detached from the live objects, so it can be sent to a service and cannot be
 * used to reach into the simulation.
 */
import { bearingDeg, distanceM, NM_TO_M, toMagneticDeg } from '@atc/core';
import type { Aircraft } from '../aircraft';
import type { Instruction } from '../commands';
import { NEEDS } from '../patience';
import type { AllowedValues } from './phrase';
import type { PilotContext } from './types';

/** How much of the sector a crew is told about. */
export const TRAFFIC_RANGE_NM = 15;
export const TRAFFIC_RANGE_FT = 3000;
export const MAX_TRAFFIC = 8;
export const MAX_FREQUENCY = 8;

export interface SituationTraffic {
  callsign: string;
  bearingDeg: number;
  distanceNm: number;
  altitudeFt: number;
  relative: 'above' | 'below' | 'level';
  /** Closing on us over the next minute at present headings. */
  closing: boolean;
}

export interface PilotSituation {
  simTimeS: number;
  callsign: string;
  own: {
    type: string;
    category: string;
    kind: string;
    phase: string;
    control?: { owner: string; frequency: string };
    altitudeFt: number;
    headingDeg: number;
    iasKt: number;
    groundspeedKt: number;
    verticalSpeedFpm: number;
    onGround: boolean;
    assigned: Aircraft['assigned'];
    requested: string;
    procedure?: string;
    approach?: { runway: string; established: boolean; cleared: boolean };
    fuel?: { enduranceMin: number; state: string };
    emergency?: { nature: string; level: string; intention: string; persons: number; acknowledged: boolean };
    persons?: number;
    /** How this crew talks: keep to it for the whole flight. */
    crewStyle: 'standard' | 'terse' | 'chatty';
  };
  memory: {
    heard: Instruction[];
    pending: Instruction[];
    waitingFor?: { need: string; stage: number; waitedS: number };
    history: { from: 'atc' | 'pilot'; text: string; agoS: number }[];
  };
  /** Fix names still ahead on the route. */
  routeAhead: string[];
  runways: { arrival: string; departure: string; approach?: string };
  traffic: SituationTraffic[];
  /** The last few transmissions on the frequency, whoever they were for. */
  frequency: { from: 'atc' | 'pilot'; callsign: string; text: string; agoS: number }[];
  allowed: AllowedValues;
}

export interface SituationExtras {
  all: readonly Aircraft[];
  radio: readonly { from: 'atc' | 'pilot'; callsign: string; text: string; timeS: number }[];
  runways: { arrival: string; departure: string; approach?: string };
  routeAhead: string[];
  allowed: AllowedValues;
}

function trafficAround(ac: Aircraft, all: readonly Aircraft[]): SituationTraffic[] {
  const out: SituationTraffic[] = [];
  for (const other of all) {
    if (other === ac || other.onGround) continue;
    const dNm = distanceM(ac.lat, ac.lon, other.lat, other.lon) / NM_TO_M;
    const dFt = other.altitudeFt - ac.altitudeFt;
    if (dNm > TRAFFIC_RANGE_NM || Math.abs(dFt) > TRAFFIC_RANGE_FT) continue;
    // Closing: the gap in a minute at present headings and speeds is smaller than it is now.
    const step = (a: Aircraft) => {
      const nmPerMin = a.groundspeedKt / 60, rad = (a.headingDeg * Math.PI) / 180;
      return { x: Math.sin(rad) * nmPerMin, y: Math.cos(rad) * nmPerMin };
    };
    const brg = bearingDeg(ac.lat, ac.lon, other.lat, other.lon);
    const rel = { x: Math.sin((brg * Math.PI) / 180) * dNm, y: Math.cos((brg * Math.PI) / 180) * dNm };
    const a = step(ac), b = step(other);
    const after = Math.hypot(rel.x + b.x - a.x, rel.y + b.y - a.y);
    out.push({
      callsign: other.callsign,
      bearingDeg: Math.round(brg),
      distanceNm: Math.round(dNm * 10) / 10,
      altitudeFt: Math.round(other.altitudeFt / 100) * 100,
      relative: dFt > 400 ? 'above' : dFt < -400 ? 'below' : 'level',
      closing: after < dNm,
    });
  }
  return out.sort((x, y) => x.distanceNm - y.distanceNm).slice(0, MAX_TRAFFIC);
}

export function situationFor(ctx: PilotContext, extras: SituationExtras): PilotSituation {
  const { ac, simTimeS } = ctx;
  const req = ac.pilot.request;
  return {
    simTimeS: Math.round(simTimeS),
    callsign: ac.callsign,
    own: {
      type: ac.type,
      category: ac.category,
      kind: ac.kind,
      phase: ac.phase,
      ...(ac.control ? { control: { owner: ac.control.owner, frequency: ac.control.frequency } } : {}),
      altitudeFt: Math.round(ac.altitudeFt),
      // Magnetic, as everything a crew says: the model reads it back.
      headingDeg: Math.round(toMagneticDeg(ctx.airport, ac.headingDeg)) || 360,
      iasKt: Math.round(ac.iasKt),
      groundspeedKt: Math.round(ac.groundspeedKt),
      verticalSpeedFpm: Math.round(ac.verticalSpeedFpm),
      onGround: ac.onGround,
      assigned: { ...ac.assigned },
      requested: ac.requested,
      ...(ac.procedure ? { procedure: ac.procedure } : {}),
      ...(ac.approach ? { approach: { runway: ac.approach.runway.name, established: ac.approach.established, cleared: ac.approach.cleared } } : {}),
      ...(ac.fuel ? { fuel: { enduranceMin: Math.round(ac.fuel.enduranceMin), state: ac.fuel.state } } : {}),
      ...(ac.emergency ? { emergency: { nature: ac.emergency.nature, level: ac.emergency.level, intention: ac.emergency.intention, persons: ac.emergency.persons, acknowledged: ac.emergency.acknowledgedAtS !== undefined } } : {}),
      ...(ac.persons !== undefined ? { persons: ac.persons } : {}),
      crewStyle: ac.crewStyle ?? 'standard',
    },
    memory: {
      heard: ac.pilot.heard.map((i) => ({ ...i })),
      pending: ac.pilot.pending.map((i) => ({ ...i })),
      ...(req && !req.unvoiced ? { waitingFor: { need: NEEDS[req.kind].text, stage: req.stage, waitedS: Math.round(simTimeS - req.askedAtS) } } : {}),
      history: ac.pilot.history.slice(-MAX_FREQUENCY).map((h) => ({ from: h.from, text: h.text, agoS: Math.round(simTimeS - h.timeS) })),
    },
    routeAhead: extras.routeAhead,
    runways: extras.runways,
    traffic: trafficAround(ac, extras.all),
    frequency: extras.radio.slice(-MAX_FREQUENCY).map((m) => ({ from: m.from, callsign: m.callsign, text: m.text, agoS: Math.round(simTimeS - m.timeS) })),
    allowed: extras.allowed,
  };
}
